import React, { useState, useEffect, useCallback } from "react";
import StepNode from "./StepNode";
import VendorParserBuilderPanel from "./VendorParserBuilderPanel";
import AccountLocationPicker from "./AccountLocationPicker";
import ChouxboxFileGroupPicker from "./ChouxboxFileGroupPicker";
import JsonModal from "./JsonModal";
import Modal from "./Modal";
import InvoicePreview from "./InvoicePreview";
import MappedInvoicePreview from "./MappedInvoicePreview";
import ValidationPreview from "./ValidationPreview";
import PdfPreviewModal from "./PdfPreviewModal";
import { uploadFiles, runStep, fetchJSON, mapData, validateAndScore, importChouxboxFileGroup } from "./api";

const Arrow = () => (
  <div className="text-center text-muted" style={{ fontSize: "20px", lineHeight: "20px" }}>
    &#9660;
  </div>
);

const INITIAL_STEP = { status: "idle", data: null, error: null };

const DISPOSITION_BADGE = {
  straight_through: "bg-success",
  form_assist: "bg-warning text-dark",
  exception: "bg-danger",
};

const UnmatchedQueueTable = ({ rows, onPreview }) => (
  <table className="table table-sm mb-0">
    <thead>
      <tr>
        <th>Filename</th>
        <th>Seen</th>
        <th>Last seen</th>
        <th></th>
      </tr>
    </thead>
    <tbody>
      {rows.map((row) => (
        <tr key={row.id}>
          <td>{row.filename}</td>
          <td>{row.occurrences_count}</td>
          <td>{new Date(row.last_seen_at).toLocaleString()}</td>
          <td>
            {row.original_file_url && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary py-0 px-2"
                style={{ fontSize: "11px" }}
                onClick={() => onPreview({ url: row.original_file_url, title: row.filename })}
              >
                Preview
              </button>
            )}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const InvoicePipelinePlayground = ({ version }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  // Step 1 source: "upload" (choose local files) or "chouxbox" (an
  // unencoded Chouxbox file group at the target location's restaurant).
  const [source, setSource] = useState("upload");
  const [importingFileGroupId, setImportingFileGroupId] = useState(null);
  const [resultModalOpen, setResultModalOpen] = useState(false);
  const [unmatchedModalOpen, setUnmatchedModalOpen] = useState(false);
  const [preview, setPreview] = useState(null); // { url, title } | null
  const [fileToken, setFileToken] = useState(null);
  const [fileMeta, setFileMeta] = useState(null);
  const [steps, setSteps] = useState({
    upload: { ...INITIAL_STEP },
    file_type: { ...INITIAL_STEP },
    vendor: { ...INITIAL_STEP },
    parser_extract: { ...INITIAL_STEP },
    no_parser: { ...INITIAL_STEP },
    textract: { ...INITIAL_STEP },
    collate: { ...INITIAL_STEP },
    map_data: { ...INITIAL_STEP },
    score_data: { ...INITIAL_STEP },
  });

  const [supportedVendors, setSupportedVendors] = useState([]);
  // null until the operator loads it -- the list is not fetched on page load.
  const [unmatchedQueue, setUnmatchedQueue] = useState(null);
  const [unmatchedQueueLoading, setUnmatchedQueueLoading] = useState(false);
  const [builderRefreshSignal, setBuilderRefreshSignal] = useState(0);

  // Step 5 target -- kept separate from `steps` since it's a picked
  // context, not a step result, and persists across file changes so the
  // operator can run several files against the same account/location.
  const [account, setAccount] = useState(null);
  const [location, setLocation] = useState(null);

  const setStep = (key, patch) => {
    setSteps((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  const refreshUnmatchedQueue = useCallback(() => {
    setUnmatchedQueueLoading(true);
    fetchJSON("unmatched_vendor_layouts")
      .then((data) => setUnmatchedQueue(data.layouts || []))
      .catch(() => {})
      .finally(() => setUnmatchedQueueLoading(false));
  }, []);

  useEffect(() => {
    fetchJSON("supported_vendors")
      .then((data) => setSupportedVendors(data.vendors || []))
      .catch(() => {});
  }, []);

  const runAndTrack = async (key, apiCall) => {
    setStep(key, { status: "running", error: null });
    try {
      const data = await apiCall();
      setStep(key, { status: "done", data, error: null });
      return data;
    } catch (e) {
      setStep(key, { status: "error", error: e.message });
      throw e;
    }
  };

  const resetPipeline = () => {
    setFileToken(null);
    setFileMeta(null);
    setSteps({
      upload: { ...INITIAL_STEP },
      file_type: { ...INITIAL_STEP },
      vendor: { ...INITIAL_STEP },
      parser_extract: { ...INITIAL_STEP },
      no_parser: { ...INITIAL_STEP },
      textract: { ...INITIAL_STEP },
      collate: { ...INITIAL_STEP },
      map_data: { ...INITIAL_STEP },
      score_data: { ...INITIAL_STEP },
    });
  };

  const handleFileChange = (e) => {
    resetPipeline();
    setSelectedFiles(Array.from(e.target.files || []));
  };

  // Multiple files are only meaningful when the first is an image (a
  // scanned invoice captured as several separate photos, one per page) --
  // a multi-page PDF is already one file and doesn't need this. Checked
  // client-side too so the operator sees this before attempting upload,
  // not just from the server's rejection.
  const multiFileError =
    selectedFiles.length > 1 && selectedFiles[0] && !selectedFiles[0].type.startsWith("image/")
      ? "Multiple files are only allowed when the first file is an image, not a PDF."
      : null;

  const handleUpload = async () => {
    if (selectedFiles.length === 0 || multiFileError) return;
    const data = await runAndTrack("upload", () => uploadFiles(selectedFiles));
    setFileToken(data.file_token);
    setFileMeta(data);
  };

  const handleImportFileGroup = async (fileGroup) => {
    resetPipeline();
    setSelectedFiles([]);
    setImportingFileGroupId(fileGroup.id);
    try {
      const data = await runAndTrack("upload", () => importChouxboxFileGroup(account.id, location.id, fileGroup.id));
      setFileToken(data.file_token);
      setFileMeta(data);
    } catch (e) {
      // runAndTrack already shows the error on Step 1.
    } finally {
      setImportingFileGroupId(null);
    }
  };

  const handleIdentifyFileType = () =>
    runAndTrack("file_type", () => runStep("identify_file_type", fileToken));

  const handleIdentifyVendor = () =>
    runAndTrack("vendor", () => runStep("identify_vendor", fileToken));

  const handleExtractWithParser = () => {
    clearMappingSteps();
    return runAndTrack("parser_extract", () => runStep("extract_with_parser", fileToken));
  };

  const handleMarkNoParser = () =>
    runAndTrack("no_parser", () => runStep("mark_no_parser", fileToken)).then((data) => {
      // Only refresh lists the operator has already loaded.
      if (unmatchedQueue !== null) refreshUnmatchedQueue();
      setBuilderRefreshSignal((n) => n + 1);
      return data;
    });

  const handleCollatePages = () => {
    clearMappingSteps();
    return runAndTrack("collate", () => runStep("collate_pages", fileToken));
  };

  const handleExtractWithTextract = () => {
    clearMappingSteps();
    return runAndTrack("textract", () => runStep("extract_with_textract", fileToken));
  };

  const handleMapData = () =>
    runAndTrack("map_data", () => mapData(fileToken, account.id, location ? location.id : null));

  const handleValidateAndScore = () =>
    runAndTrack("score_data", () => validateAndScore(fileToken, account.id, location ? location.id : null));

  // Steps 5/6 results belong to the account/location they ran against.
  const clearMappingSteps = () => {
    setStep("map_data", { ...INITIAL_STEP });
    setStep("score_data", { ...INITIAL_STEP });
  };

  const handleAccountChange = (nextAccount) => {
    setAccount(nextAccount);
    setLocation(null);
    clearMappingSteps();
  };

  const handleLocationChange = (nextLocation) => {
    setLocation(nextLocation);
    clearMappingSteps();
  };

  const hasExtraction = Boolean(steps.parser_extract.data || steps.textract.data);

  const detectedType = steps.file_type.data && steps.file_type.data.detection && steps.file_type.data.detection.type;
  const fileKind = steps.file_type.data && steps.file_type.data.file_kind;
  const needsCollation =
    fileMeta && fileMeta.page_count > 1 && !(steps.collate.data && steps.collate.data.collated);

  const finalResult = {
    file: fileMeta,
    file_type: steps.file_type.data,
    vendor: steps.vendor.data,
    parser_extract: steps.parser_extract.data,
    no_parser: steps.no_parser.data,
    textract: steps.textract.data,
    map_data: steps.map_data.data,
    score_data: steps.score_data.data,
  };

  const hasAnyResult = Object.values(finalResult).some(
    (v) => v !== null && v !== undefined && v !== "not implemented yet"
  );

  return (
    <div className="d-flex flex-column align-items-center" style={{ gap: "8px", padding: "16px 0" }}>
      <AccountLocationPicker
        account={account}
        location={location}
        onAccountChange={handleAccountChange}
        onLocationChange={handleLocationChange}
      />

      <Arrow />

      <div className="card" style={{ minWidth: "320px", maxWidth: source === "chouxbox" ? "700px" : "420px", width: "100%" }}>
        <div className="card-body">
          <h5 className="card-title">Invoice Pipeline Playground</h5>
          <p className="card-text small text-muted">
            Upload a file -- or pick an unencoded upload from Chouxbox -- then trigger each
            pipeline step one at a time to see how it flows through detection, vendor matching,
            extraction, mapping and scoring.
          </p>
          <ul className="nav nav-tabs small mb-2">
            {[
              ["upload", "Upload file"],
              ["chouxbox", "From Chouxbox"],
            ].map(([key, label]) => (
              <li className="nav-item" key={key}>
                <button
                  type="button"
                  className={`nav-link py-1 px-2 ${source === key ? "active" : ""}`}
                  onClick={() => setSource(key)}
                >
                  {label}
                </button>
              </li>
            ))}
          </ul>
          {source === "upload" && (
            <>
              <p className="small text-muted mb-2">
                Select multiple files only when the first is an image -- a scanned invoice captured
                as several separate photos, one per page.
              </p>
              <input
                type="file"
                className="form-control form-control-sm mb-2"
                accept=".pdf,image/*"
                multiple
                onChange={handleFileChange}
              />
            </>
          )}
          {source === "chouxbox" && (
            <div className="mb-2">
              <ChouxboxFileGroupPicker
                account={account}
                location={location}
                disabled={steps.upload.status === "running"}
                importingId={importingFileGroupId}
                onImport={handleImportFileGroup}
                onPreview={setPreview}
              />
            </div>
          )}
          {source === "upload" && selectedFiles.length > 0 && !fileToken && (
            <div className="small text-muted mb-2">
              Selected: {selectedFiles.map((f) => f.name).join(", ")}
            </div>
          )}
          {multiFileError && <div className="small text-danger mb-2">{multiFileError}</div>}
          {fileToken && (
            <div className="d-flex" style={{ gap: "8px" }}>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={resetPipeline}>
                Reset pipeline
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() =>
                  setPreview({
                    url: `/playground/invoice_pipeline/preview_file?file_token=${encodeURIComponent(fileToken)}`,
                    title: (fileMeta && fileMeta.filename) || "Uploaded file",
                  })
                }
              >
                Preview file
              </button>
            </div>
          )}
        </div>
      </div>

      <StepNode
        number="1"
        title="Receive file"
        description={
          source === "chouxbox"
            ? "Download the chosen Chouxbox file group: its original PDF if it has one, otherwise its page images."
            : "Upload the invoice file(s) to the server."
        }
        status={steps.upload.status}
        onRun={source === "upload" && selectedFiles.length > 0 && !fileToken && !multiFileError ? handleUpload : null}
        runLabel="Upload file"
        result={steps.upload.data}
        error={steps.upload.error}
        disabledReason={
          source === "chouxbox"
            ? fileToken
              ? null
              : 'Click "Use this" on a file group above.'
            : selectedFiles.length === 0
            ? "Choose a file above first."
            : multiFileError
        }
      />

      <Arrow />

      <StepNode
        number="2"
        title="Identify file type"
        description="PDF (system-generated) vs. PDF (scanned) vs. image."
        status={steps.file_type.status}
        onRun={handleIdentifyFileType}
        result={steps.file_type.data}
        error={steps.file_type.error}
        disabledReason={!fileToken ? "Upload a file first (step 1)." : null}
      />

      <Arrow />

      <div className="d-flex justify-content-center" style={{ gap: "32px", flexWrap: "wrap" }}>
        <div className="d-flex flex-column align-items-center" style={{ gap: "8px" }}>
          <div className="badge bg-light text-dark border">PDF - system generated</div>

          {supportedVendors.length > 0 && (
            <div className="small text-muted text-center" style={{ maxWidth: "260px" }}>
              Parsers exist for: {supportedVendors.map((v) => v.vendor_name).join(", ")}
            </div>
          )}

          <StepNode
            number="3.1 / 3.2"
            title="Identify vendor"
            description="Check whether a vendor-specific parser matches this layout."
            status={steps.vendor.status}
            onRun={handleIdentifyVendor}
            result={steps.vendor.data}
            error={steps.vendor.error}
            disabledReason={!fileToken ? "Upload a file first (step 1)." : null}
          />

          <Arrow />

          <StepNode
            number="3.2.1"
            title="Extract with vendor parser"
            description="Run when a parser was found."
            status={steps.parser_extract.status}
            onRun={handleExtractWithParser}
            result={steps.parser_extract.data}
            error={steps.parser_extract.error}
            disabledReason={!fileToken ? "Upload a file first (step 1)." : null}
            renderPreview={(result) => <InvoicePreview data={result.data || {}} />}
          />

          <Arrow />

          <StepNode
            number="3.3.1"
            title="Mark: no parser available"
            description="Run when no parser was found, so it can be built later."
            status={steps.no_parser.status}
            onRun={handleMarkNoParser}
            result={steps.no_parser.data}
            error={steps.no_parser.error}
            disabledReason={!fileToken ? "Upload a file first (step 1)." : null}
          />
        </div>

        <div className="d-flex flex-column align-items-center" style={{ gap: "8px" }}>
          <div className="badge bg-light text-dark border">PDF scanned / image</div>

          <StepNode
            number="4.1"
            title="Collate pages"
            description="Combine multiple uploaded pages into one document before OCR. A pass-through for a single page (whether that's a PDF or one image)."
            status={steps.collate.status}
            onRun={handleCollatePages}
            result={steps.collate.data}
            error={steps.collate.error}
            disabledReason={!fileToken ? "Upload a file first (step 1)." : null}
          />

          <Arrow />

          <StepNode
            number="3.3.2 / 4.2"
            title="Extract with AWS Textract"
            description="OCR fallback for PDFs with no parser, and for scanned/image files."
            status={steps.textract.status}
            onRun={handleExtractWithTextract}
            result={steps.textract.data}
            error={steps.textract.error}
            disabledReason={
              !fileToken
                ? "Upload a file first (step 1)."
                : needsCollation
                ? 'Run "Collate pages" (step 4.1) first.'
                : null
            }
          />
        </div>
      </div>

      {fileToken && fileKind && (
        <div className="small text-muted">
          Detected: {fileKind}
          {detectedType ? ` / ${detectedType}` : ""}
          {fileMeta && fileMeta.page_count > 1 ? ` (${fileMeta.page_count} pages uploaded)` : ""}
        </div>
      )}

      <div className="card" style={{ minWidth: "260px", maxWidth: "600px", width: "100%" }}>
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-start">
            <h6 className="card-title">Vendors waiting on a parser</h6>
            {unmatchedQueue && unmatchedQueue.length > 0 && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary py-0 px-2"
                style={{ fontSize: "11px" }}
                onClick={() => setUnmatchedModalOpen(true)}
              >
                View larger
              </button>
            )}
          </div>
          <p className="card-text small text-muted mb-2">
            Persisted every time "Mark: no parser available" (step 3.3.1) runs. Same layout
            seen again just bumps the count instead of adding a new row.
          </p>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary py-0 px-2 mb-2"
            style={{ fontSize: "11px" }}
            disabled={unmatchedQueueLoading}
            onClick={refreshUnmatchedQueue}
          >
            {unmatchedQueueLoading ? "Loading..." : unmatchedQueue === null ? "Load list" : "Refresh"}
          </button>
          {unmatchedQueue === null ? null : unmatchedQueue.length > 0 ? (
            <div style={{ maxHeight: "260px", overflowY: "auto" }}>
              <UnmatchedQueueTable rows={unmatchedQueue} onPreview={setPreview} />
            </div>
          ) : (
            <div className="text-muted small">Nothing flagged yet.</div>
          )}
        </div>
      </div>

      <Modal
        open={unmatchedModalOpen}
        onClose={() => setUnmatchedModalOpen(false)}
        title="Vendors waiting on a parser"
        maxWidth="900px"
      >
        <div className="p-3">
          <UnmatchedQueueTable rows={unmatchedQueue || []} onPreview={setPreview} />
        </div>
      </Modal>

      <PdfPreviewModal
        open={!!preview}
        onClose={() => setPreview(null)}
        title={preview ? preview.title : ""}
        url={preview ? preview.url : null}
      />

      <Arrow />

      <VendorParserBuilderPanel refreshSignal={builderRefreshSignal} />

      <Arrow />

      <StepNode
        number="5"
        title="Map data"
        description="Normalize extracted fields into a common shape by running the real Layers::Synthesize / Layers::R365Synthesize pipeline."
        status={steps.map_data.status}
        onRun={handleMapData}
        result={steps.map_data.data}
        error={steps.map_data.error}
        disabledReason={
          !fileToken
            ? "Upload a file first (step 1)."
            : !hasExtraction
            ? 'Extract data first -- "Extract with vendor parser" (step 3.2.1) or "Extract with AWS Textract" (step 3.3.2 / 4.2).'
            : !account
            ? "Pick a target account above."
            : null
        }
        renderPreview={(result) => <MappedInvoicePreview data={result.data || {}} />}
      />

      <Arrow />

      <StepNode
        number="6"
        title="Validate and score data"
        description="Classify each field's confidence and derive a straight_through / form_assist / exception disposition."
        status={steps.score_data.status}
        onRun={handleValidateAndScore}
        result={steps.score_data.data}
        error={steps.score_data.error}
        disabledReason={
          !fileToken
            ? "Upload a file first (step 1)."
            : !account
            ? "Pick a target account above."
            : !steps.map_data.data
            ? 'Run "Map data" (step 5) first.'
            : null
        }
        renderPreview={(result) => <ValidationPreview data={result.validation || {}} />}
      />

      {steps.score_data.data && steps.score_data.data.validation && (
        <div className="small text-center">
          <span className={`badge ${DISPOSITION_BADGE[steps.score_data.data.validation.disposition] || "bg-secondary"}`}>
            {steps.score_data.data.validation.disposition}
          </span>
          <span className="text-muted ms-2">
            score {steps.score_data.data.validation.score} · {steps.score_data.data.validation.hard_failures.length} hard
            failure(s) · {steps.score_data.data.validation.review_signals.length} review signal(s)
          </span>
        </div>
      )}

      <Arrow />

      <div className="card" style={{ minWidth: "260px", maxWidth: "600px", width: "100%" }}>
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-start">
            <h6 className="card-title">Step 7 - Result so far</h6>
            {hasAnyResult && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary py-0 px-2"
                style={{ fontSize: "11px" }}
                onClick={() => setResultModalOpen(true)}
              >
                View full JSON
              </button>
            )}
          </div>
          <p className="card-text small text-muted mb-2">
            Live view of everything collected from the steps run above.
          </p>
          {hasAnyResult ? (
            <pre
              style={{
                background: "#f8f9fa",
                padding: "8px",
                borderRadius: "4px",
                maxHeight: "320px",
                overflow: "auto",
                fontSize: "12px",
                marginBottom: 0,
              }}
            >
              {JSON.stringify(finalResult, null, 2)}
            </pre>
          ) : (
            <div className="text-muted small">Nothing collected yet.</div>
          )}
          <JsonModal
            open={resultModalOpen}
            onClose={() => setResultModalOpen(false)}
            title="Step 7 — Result so far"
            data={finalResult}
          />
        </div>
      </div>

      {version && (
        <div className="small text-muted text-center mt-2" style={{ opacity: 0.7 }}>
          {version.branch}@{version.sha}
          {version.commit_at ? ` · committed ${new Date(version.commit_at).toLocaleString()}` : ""}
          {version.booted_at ? ` · server booted ${new Date(version.booted_at).toLocaleString()}` : ""}
        </div>
      )}
    </div>
  );
};

export default InvoicePipelinePlayground;
